import React from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import CtaSection from '../components/CtaSection';
import SEO from '../components/SEO';
const SERVICES = [
  {
    n:'01', ico:'⚙️', title:'Backend Development',
    note:'Ideal for: SaaS platforms, internal tools, API-driven products, scaling teams.',
    desc:'We build secure, scalable backend systems using Python, Django, FastAPI, PostgreSQL, and REST APIs. From new greenfield projects to codebase takeovers — we own the full backend lifecycle.',
    tags:['Django','FastAPI','PostgreSQL','REST APIs','JWT Auth'],
    feats:['Django REST Framework and FastAPI development','REST API design, versioning, and documentation','PostgreSQL schema design and query optimization','Authentication, authorization, and role management','Performance optimization and load testing','Existing codebase audit, refactor, and takeover','Unit, integration, and end-to-end testing suites'],
  },
  {
    n:'02', ico:'🤖', title:'AI & LLM Integration',
    note:'Ideal for: automation-heavy teams, knowledge management, data extraction at scale.',
    desc:'We integrate AI into real business workflows — not demos. Using OpenAI, local LLMs via Ollama, and structured output pipelines, we build AI that genuinely replaces manual work.',
    tags:['OpenAI','Ollama','RAG','LangChain','Embeddings'],
    feats:['OpenAI API integration with retry logic and cost control','Local LLM deployment with Ollama on-premise','Prompt engineering and system prompt optimization','Structured JSON extraction from unstructured text','Retrieval-augmented generation (RAG) pipelines','AI-powered Q&A and knowledgebase assistants','Internal AI tools for operations and support teams'],
  },
  {
    n:'03', ico:'📄', title:'AI Document Processing',
    note:'Ideal for: finance teams, operations, procurement, any paper-heavy workflow.',
    desc:'We automate document-heavy workflows using OCR + AI to extract structured data from invoices, PDFs, contracts, and enterprise documents — with human-in-the-loop review where needed.',
    tags:['OCR','NLP','PDF Processing','Confidence Scoring'],
    feats:['OCR pipeline development (Tesseract, AWS Textract)','Invoice field extraction: vendor, date, totals, line items','Contract and policy document parsing','Confidence scoring and exception routing','Human-in-the-loop review UI and approval flows','API, webhook, and CSV integration with accounting systems','Batch processing for high document volume'],
  },
  {
    n:'04', ico:'☁️', title:'Cloud & DevOps',
    note:'Ideal for: teams moving to cloud, startups scaling infra, legacy migrations.',
    desc:'We deploy and maintain backend systems on AWS with clean, repeatable workflows — Docker, GitHub Actions, staging/production parity, and observability from day one.',
    tags:['AWS','Docker','EC2','RDS','Lambda'],
    feats:['AWS EC2, RDS, S3, Lambda, CloudWatch setup','Docker-based containerized deployments','GitHub Actions CI/CD pipeline configuration','Staging and production environment management','SSL, DNS, load balancing, and auto-scaling','Monitoring, alerting, and logging (CloudWatch, Sentry)','Cost optimization and performance improvements'],
  },
  {
    n:'05', ico:'🔄', title:'Workflow Automation',
    note:'Ideal for: ops teams, SaaS companies, businesses with repetitive manual processes.',
    desc:'We automate business workflows using APIs, n8n, backend services, and event-driven architectures — replacing hours of manual work with reliable, auditable pipelines.',
    tags:['n8n','Webhooks','Event-Driven','Celery'],
    feats:['n8n workflow design, deployment, and maintenance','Webhook integration across third-party platforms','API-to-API automation pipelines','Data routing, transformation, and enrichment','Notification and alert workflow systems','Celery task queues for async processing','Internal operations automation and reporting'],
  },
  {
    n:'06', ico:'📊', title:'Data Engineering & Analytics',
    note:'Ideal for: data-driven products, analytics teams, reporting automation.',
    desc:'We build pipelines that process, transform, and surface business data — from raw ingestion to actionable dashboards and reporting systems that run reliably at scale.',
    tags:['Airflow','ETL','SQL','DynamoDB','MLflow'],
    feats:['Apache Airflow DAG design and deployment','ETL/ELT pipeline development and orchestration','SQL query optimization and schema redesign','Dashboard backend services and data APIs','External API ingestion and data normalization','MLflow model tracking, deployment, and versioning','Automated reporting and scheduled data exports'],
  },
];

const INDUSTRY = [
  {
    label:'⚡ Solar & Energy',
    title:'Software for Solar & Energy Platforms',
    desc:'Five years of hands-on experience building backend systems for large-scale solar engineering workflows — simulation, optimization, and file generation at 100MW+ scale.',
    points:['Systems supporting 100+ sites and ~100MW projects','NASA and NREL weather API integrations','DXF/CAD, KMZ, and PVSyst SHD export pipelines','Solar performance and environmental shade analysis','Simulation optimization backend services'],
  },
  {
    label:'🏢 Business Operations',
    title:'AI Automation for Operations Teams',
    desc:'We help businesses eliminate manual bottlenecks by building AI layers that plug directly into existing operations — without disrupting what already works.',
    points:['Invoice and accounts payable automation','Policy and compliance knowledgebase assistant','Internal document search and retrieval','Supplier document processing pipelines','ERP data entry and sync automation'],
  },
];

export default function Services() {
  useReveal();
  return (
    <div className="page">
      <SEO
  title="Services — Backend Engineering, AI Automation & Cloud Infrastructure | Arhatinfo"
  description="Backend engineering, AI automation, API development, cloud infrastructure, and SaaS development using Python, Django, FastAPI, OpenAI, and AWS."
  keywords="backend engineering services, AI automation, Django development, API development, cloud infrastructure, SaaS development"
  canonical="https://arhatinfo.com/services"
  og={{ title:"Arhatinfo Services — Backend Engineering & AI Automation", url:"https://arhatinfo.com/services" }}
/>
      <div className="ph rv">
        <div className="s-label">What We Offer</div>
        <h1>Services Built for<br />Production-Grade Systems</h1>
        <p className="lead">From backend architecture to AI automation — every engagement delivers reliable, scalable software that works in the real world.</p>
      </div>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="svc-full-list">
            {SERVICES.map((s, i) => (
              <div key={i} className={`svc-full-item rv d${(i % 2) + 1}`}>
                <div className="sfl">
                  <div className="snum">SERVICE {s.n}</div>
                  <div className="svc-icon" style={{ marginBottom: '1rem' }}>{s.ico}</div>
                  <h2>{s.title}</h2>
                  <p>{s.desc}</p>
                  <p style={{ fontSize: '.83rem', color: 'var(--dim)' }}>{s.note}</p>
                  <div className="tags" style={{ marginTop: '1.2rem' }}>
                    {s.tags.map(t => <span key={t} className="tag">{t}</span>)}
                  </div>
                </div>
                <div className="sfr">
                  <h4>Capabilities</h4>
                  <ul className="feat-list">
                    {s.feats.map(f => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry */}
      <section className="section" style={{ background: 'var(--bg2)', paddingTop: '80px', paddingBottom: '80px' }}>
        <div className="wrap">
          <div className="s-head rv">
            <div className="s-label">Specialized Domains</div>
            <h2 className="s-title">Deep Industry Expertise</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            {INDUSTRY.map((ind, i) => (
              <div key={i} className={`rv d${i + 1}`} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '2.5rem', transition: 'all .3s', position: 'relative', overflow: 'hidden' }}>
                <div style={{ fontSize: '.72rem', fontWeight: 700, letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--blue-hi)', marginBottom: '1rem' }}>{ind.label}</div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--white)', marginBottom: '.8rem' }}>{ind.title}</h3>
                <p style={{ fontSize: '.88rem', color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1.4rem' }}>{ind.desc}</p>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                  {ind.points.map(p => (
                    <li key={p} style={{ fontSize: '.85rem', color: 'var(--muted)', display: 'flex', gap: '.6rem' }}>
                      <span style={{ color: 'var(--cyan)', flexShrink: 0 }}>✓</span>{p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
